import React from 'react';
import { FormField, TextInput, SelectInput, RadioGroup, NavButtons } from './QuoteFormField';

const LOSS_TYPES = ['Auto Collision', 'Auto Theft', 'Water Damage', 'Fire', 'Wind/Hail', 'Liability', 'Workers Comp', 'Other'];

export default function QuoteStepLossHistory({ form, onChange, onNext, onBack }) {
  const losses = form.loss_history || [];

  const addLoss = () => {
    if (losses.length < 5) {
      onChange({ loss_history: [...losses, { date: '', type: '', description: '', amount: '' }] });
    }
  };

  const updateLoss = (i, updates) => {
    const updated = [...losses];
    updated[i] = { ...updated[i], ...updates };
    onChange({ loss_history: updated });
  };

  const removeLoss = (i) => {
    onChange({ loss_history: losses.filter((_, idx) => idx !== i) });
  };

  const handleNext = () => {
    if (form.has_prior_losses && losses.length === 0) return;
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Loss History</h2>
        <p className="text-gray-500">Please answer the following questions honestly — carriers check these records.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Have you had any insurance claims in the past 5 years? <span className="text-red-500">*</span>
        </label>
        <RadioGroup
          value={form.has_prior_losses ? 'Yes' : 'No'}
          onChange={(val) => {
            onChange({ has_prior_losses: val === 'Yes', loss_history: val === 'No' ? [] : losses });
          }}
          options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
        />
      </div>

      {form.has_prior_losses && (
        <div className="space-y-4">
          {losses.length === 0 && (
            <p className="text-sm text-red-600">Please add at least one loss entry.</p>
          )}
          {losses.map((loss, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Loss {i + 1}</span>
                <button onClick={() => removeLoss(i)} className="text-xs text-red-500 hover:underline">Remove</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Date of Loss">
                  <TextInput type="date" value={loss.date} onChange={val => updateLoss(i, { date: val })} />
                </FormField>
                <FormField label="Type of Loss">
                  <SelectInput value={loss.type} onChange={val => updateLoss(i, { type: val })} options={LOSS_TYPES} placeholder="Select type" />
                </FormField>
              </div>
              <FormField label="Brief Description">
                <TextInput value={loss.description} onChange={val => updateLoss(i, { description: val })} placeholder="Describe what happened" />
              </FormField>
              <FormField label="Approximate Amount Paid ($)">
                <TextInput value={loss.amount} onChange={val => updateLoss(i, { amount: val })} placeholder="5,000" />
              </FormField>
            </div>
          ))}
          {losses.length < 5 && (
            <button
              onClick={addLoss}
              className="w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-sm text-gray-500 hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-colors"
            >
              + Add Another Loss
            </button>
          )}
        </div>
      )}

      <div className="space-y-2 pt-2">
        <label className="block text-sm font-medium text-gray-700">
          Have you ever had insurance cancelled or non-renewed? <span className="text-red-500">*</span>
        </label>
        <RadioGroup
          value={form.prior_cancellation ? 'Yes' : 'No'}
          onChange={(val) => onChange({ prior_cancellation: val === 'Yes' })}
          options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
        />
        {form.prior_cancellation && (
          <div className="mt-3">
            <FormField label="Please explain">
              <textarea
                value={form.prior_cancellation_explanation}
                onChange={e => onChange({ prior_cancellation_explanation: e.target.value })}
                rows={3}
                placeholder="Explain what happened..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] resize-none"
              />
            </FormField>
          </div>
        )}
      </div>

      <NavButtons
        onBack={onBack}
        onNext={handleNext}
        disabled={form.has_prior_losses && losses.length === 0}
      />
    </div>
  );
}
